import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { PaperPlaneRight, Robot } from '@phosphor-icons/react';
import ReactMarkdown from 'react-markdown';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const API = `${BACKEND_URL}/api`;

const AICopilot = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hello! I am your NGO Operations AI Assistant. I can help you with:\n\n- Grant utilization analysis\n- Expense tracking and insights\n- Upcoming deadlines\n- Budget recommendations\n- Report summaries\n\nWhat would you like to know?'
    }
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = { role: 'user', content: input };
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ai/chat`, {
        message: input,
        session_id: 'default'
      });

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      const errorMessage = { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickQuestions = [
    'Which grants are underutilized?',
    'Show me expenses for the last month',
    'What reports are due this month?',
    'Summary of active grants'
  ];

  return (
    <div className="h-full flex flex-col bg-background text-foreground transition-colors duration-300">
      <div className="border-b border-border p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-[#E04E39]/15 rounded-md">
            <Robot size={32} weight="duotone" className="text-[#E04E39]" />
          </div>
          <div>
            <h1 className="text-3xl sm:text-5xl font-bold tracking-tight">AI Copilot</h1>
            <p className="text-sm sm:text-base text-muted-foreground">Your intelligent operations assistant</p>
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4 sm:p-6 space-y-4">
        {messages.map((message, idx) => (
          <div
            key={idx}
            className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
            data-testid={`message-${idx}`}
          >
            <div
              className={`max-w-[90%] sm:max-w-[80%] p-4 rounded-md shadow-md transition-all ${
                message.role === 'user'
                  ? 'bg-[#E04E39] text-white'
                  : 'bg-card border border-border text-foreground'
              }`}
            >
              {message.role === 'assistant' && (
                <div className="flex items-center gap-2 mb-3 border-b border-border pb-2">
                  <Robot size={16} weight="duotone" className="text-[#E04E39]" />
                  <span className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold">VANCO SECURE AI</span>
                </div>
              )}
              <div className="text-sm leading-relaxed prose prose-sm dark:prose-invert max-w-none">
                <ReactMarkdown
                  components={{
                    table: ({node, ...props}) => (
                      <div className="overflow-x-auto my-4">
                        <table className="min-w-full divide-y divide-border border border-border rounded-lg overflow-hidden" {...props} />
                      </div>
                    ),
                    th: ({node, ...props}) => <th className="px-3 py-2 bg-muted text-left text-xs font-bold uppercase tracking-wider" {...props} />,
                    td: ({node, ...props}) => <td className="px-3 py-2 border-t border-border text-xs" {...props} />,
                    ul: ({node, ...props}) => <ul className="list-disc ml-4 space-y-1 my-2" {...props} />,
                    ol: ({node, ...props}) => <ul className="list-decimal ml-4 space-y-1 my-2" {...props} />,
                    p: ({node, ...props}) => <p className="mb-2 last:mb-0" {...props} />,
                  }}
                >
                  {message.content}
                </ReactMarkdown>
              </div>
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-card border border-border p-4 rounded-md shadow-sm">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-[#E04E39] rounded-full animate-bounce"></div>
                <div className="w-2 h-2 bg-[#E04E39] rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                <div className="w-2 h-2 bg-[#E04E39] rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="border-t border-border p-4 sm:p-6 space-y-4 bg-muted/20">
        <div className="hidden sm:block">
          <p className="text-[10px] text-muted-foreground uppercase tracking-widest font-bold mb-2">Quick Intelligence</p>
          <div className="flex flex-wrap gap-2">
            {quickQuestions.map((question, idx) => (
              <button
                key={idx}
                onClick={() => setInput(question)}
                className="px-3 py-1.5 bg-card border border-border rounded-full text-xs text-muted-foreground hover:text-foreground hover:border-[#E04E39] transition-all shadow-sm"
              >
                {question}
              </button>
            ))}
          </div>
        </div>

        <div className="flex gap-2 relative">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask VANCO AI..."
            rows={1}
            className="flex-1 px-4 py-4 bg-card border border-border rounded-xl text-foreground placeholder-muted-foreground resize-none focus:border-[#E04E39] focus:ring-1 focus:ring-[#E04E39]/20 transition-all outline-none text-sm shadow-inner"
          />
          <button
            onClick={sendMessage}
            disabled={loading || !input.trim()}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-3 bg-[#E04E39] hover:bg-[#E86B5A] text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed shadow-lg transition-all"
          >
            <PaperPlaneRight size={18} weight="bold" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AICopilot;
